import React from 'react';
import { Chip } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Inspection-first verdict framing (doc §5:400): "in / out of spec" — NO
// sigma/Cpk vocabulary at the entry/inspection level. This is deliberately
// NOT msaFormat's acceptable/conditional/unacceptable vocabulary (that's a
// GRR-study acceptance band, a different concept).
//
// hasSpec/hasUsl/hasLsl below use explicit `!= null && !== ''` checks, never
// truthiness (`if (lsl)`) — a valid LSL of 0 is falsy and a truthiness check
// would silently drop a one-sided lower spec at zero.
// ---------------------------------------------------------------------------

function isSpecValue(v) {
    return v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
}

/** True when at least one of usl/lsl is a real spec value (one-sided is valid). */
export function hasSpec({ usl, lsl } = {}) {
    return isSpecValue(usl) || isSpecValue(lsl);
}

/**
 * value vs usl/lsl (doc §2.6 state 1: "verdict = value vs usl/lsl"). Handles
 * one-sided specs (only USL, or only LSL) and an LSL of exactly 0 correctly.
 * @returns {'in'|'out'|null} null when there is no spec to judge against.
 */
export function computeVerdict(value, { usl, lsl } = {}) {
    if (!hasSpec({ usl, lsl })) return null;
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    const overUsl = isSpecValue(usl) && v > Number(usl);
    const underLsl = isSpecValue(lsl) && v < Number(lsl);
    return (overUsl || underLsl) ? 'out' : 'in';
}

/** % of readings in spec, over readings that have a verdict (spec present).
 * Client-side fallback only — the GET /api/features/:id/measurements response
 * carries its own server-computed `pctInSpec`; prefer that (mapConformance
 * below) whenever a real response is in hand. */
export function percentInSpec(readings, specLimits) {
    const judged = readings
        .map((r) => computeVerdict(r.value, specLimits))
        .filter((v) => v !== null);
    if (judged.length === 0) return null;
    const inCount = judged.filter((v) => v === 'in').length;
    return (inCount / judged.length) * 100;
}

/**
 * The ONE adapter point between the wire vocab and this module's UI vocab.
 * GET/POST /api/features/:id/measurements annotate each point server-side
 * with `conformance: 'in_spec'|'out_of_spec'|null` (backend/measurementRoutes.js
 * computeVerdict) — never recompute that judgment client-side from raw
 * value/usl/lsl once a server response is in hand. `computeVerdict` above stays
 * reserved for the one place that legitimately has no server response yet: the
 * optimistic flash in PartMeasurementEntry.js.
 * @param {'in_spec'|'out_of_spec'|null|undefined} conformance
 * @returns {'in'|'out'|null}
 */
export function mapConformance(conformance) {
    if (conformance === 'in_spec') return 'in';
    if (conformance === 'out_of_spec') return 'out';
    return null;
}

/** Small chip: IN SPEC (green) / OUT OF SPEC (red). No verdict -> neutral dash. */
export function VerdictChip({ verdict, size = 'small' }) {
    if (verdict === 'in') {
        return (
            <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important', color: '#81c784 !important' }} />}
                label="In spec"
                size={size}
                sx={{ backgroundColor: 'rgba(129,199,132,0.15)', border: '1px solid rgba(129,199,132,0.4)', color: '#81c784', fontWeight: 600, fontSize: '0.72rem' }}
            />
        );
    }
    if (verdict === 'out') {
        return (
            <Chip
                icon={<ErrorIcon sx={{ fontSize: '0.85rem !important', color: '#e57373 !important' }} />}
                label="Out of spec"
                size={size}
                sx={{ backgroundColor: 'rgba(229,115,115,0.15)', border: '1px solid rgba(229,115,115,0.4)', color: '#e57373', fontWeight: 600, fontSize: '0.72rem' }}
            />
        );
    }
    return <Chip label="—" size={size} sx={{ color: 'rgba(255,255,255,0.4)' }} />;
}

/** Fixed-decimal number; '—' when null/undefined/non-finite (mirrors msaFormat.fmt). */
export function fmtNum(v, decimals = 4) {
    if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
    return Number(v).toFixed(decimals);
}

const partsFormat = { hasSpec, computeVerdict, percentInSpec, mapConformance, VerdictChip, fmtNum };
export default partsFormat;
